import React from 'react';
import { formatBillingFrequency } from 'utils/MembershipUtils';
import {
    Box,
    TableCell,
    Typography,
    makeStyles,
    Theme,
    createStyles,
    IconButton,
    TableRow,
    useTheme,
    useMediaQuery
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxTaped, faXmark, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { Membership, MembershipItem } from '@spike/membership-model';
import { useCommonStyles as useTableStyles } from './Table/CommonStyles';
import Table from './Table';
import clsx from 'clsx';

interface MembershipDetailsModalProps {
    open: boolean;
    onClose: () => void;
    membership: Membership;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modal: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: theme.spacing(4),
            zIndex: 10010,
            width: '80%',
            maxWidth: 675,
            boxShadow: theme.shadows[5],
            borderRadius: 30,
            [theme.breakpoints.down('sm')]: {
                top: 0,
                left: 0,
                transform: 'none',
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                borderRadius: 0,
                boxShadow: 'none',
                padding: 20,
                overflowY: 'auto'
            }
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 10010
        },
        header: {
            paddingTop: 25,
            marginBottom: 15,
            display: 'block'
        },
        tableContainer: {
            marginTop: 0
        },
        total: {
            marginTop: theme.spacing(2),
            display: 'flex',
            justifyContent: 'flex-end',
            fontWeight: 600
        },
        cellName: {
            width: '300px',
            maxWidth: '300px',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            [theme.breakpoints.down('sm')]: {
                width: '200px',
                maxWidth: '200px'
            }
        },
        headerCellSlim: {
            '&:first-child': {
                paddingLeft: 0
            },
            '&:last-child': {
                paddingRight: 0
            }
        },
        cellSlim: {
            '&:first-child': {
                paddingLeft: 0
            },
            '&:last-child': {
                paddingRight: 0
            }
        },
        rightAlign: {
            textAlign: 'right'
        },
        closeButton: {
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#000',
            [theme.breakpoints.down('sm')]: {
                top: 10,
                right: 10
            }
        },
        headerTitle: {
            fontSize: 20,
            fontWeight: 600,
            color: '#000'
        },
        subscriptionType: {
            marginBottom: 15,
            display: 'flex',
            gap: 40,
            paddingTop: 25,
            paddingBottom: 0,
            [theme.breakpoints.down('sm')]: {
                gap: 20,
                flexDirection: 'column',
                marginBottom: 25
            }
        },
        itemType: {
            fontSize: 14,
            fontWeight: 400,
            color: '#7A7A7A',
            marginTop: 5,
            display: 'block'
        },
        subscriptionTypeItem: {
            position: 'relative'
        },
        subscriptionTypeItemLabel: {
            fontSize: 16,
            fontWeight: 600,
            color: '#000',
            marginBottom: 5
        },
        subscriptionTypeItemValue: {
            fontSize: 16,
            fontWeight: 400
        },
        subscriptionTypeItemIcon: {
            marginRight: 4
        },
        mobileItemLabel: {
            [theme.breakpoints.down('sm')]: {
                marginBottom: 0
            }
        },
        mobileItemType: {
            [theme.breakpoints.down('sm')]: {
                marginTop: '-1px',
                marginBottom: '8px'
            }
        },
        totalValue: {
            fontWeight: 400,
            marginLeft: 5
        },
        sectionTitle: {
            fontSize: 16,
            fontWeight: 500,
            marginTop: 16,
            marginBottom: 0,
            color: '#000000'
        }
    })
);

const MembershipDetailsModal: React.FC<MembershipDetailsModalProps> = ({ open, onClose, membership }) => {
    const classes = useStyles();
    const tableStyles = useTableStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Format rollover usage display
    const getRolloverUsageDisplay = (): string => {
        const rolloverUsage = membership.rolloverUsage;

        if (rolloverUsage === 'reset') {
            return 'Reset';
        } else if (rolloverUsage === 'rollover_indefinitely') {
            return 'Rollover Indefinitely';
        } else if (rolloverUsage === 'rollover_number_of_cycles') {
            const cycles = membership.rolloverBillingCycles;
            return `Rollover for ${cycles} ${cycles === 1 ? 'Cycle' : 'Cycles'}`;
        }

        // Fallback for any unexpected value
        return rolloverUsage.charAt(0).toUpperCase() + rolloverUsage.slice(1);
    };

    // Format package expiration display
    const getPackageExpirationDisplay = (): string => {
        const packageExpiration = membership.packageExpiration;

        if (!packageExpiration || packageExpiration === 'never') {
            return 'Never Expires';
        }

        if (packageExpiration === 'limited') {
            const value = membership.packageExpirationValue;
            const unit = membership.packageExpirationUnit;

            if (value && unit) {
                const unitLabel = value === 1 ? unit.slice(0, -1) : unit; // Singular/plural
                return `${value} ${unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}`;
            }
        }

        return 'Never Expires';
    };

    // Separate items into regular and discounted
    const regularItems = React.useMemo(() => {
        return (
            membership.membershipItems?.filter(item => {
                const discountAmount = Number(item.discountAmount || 0);
                const discountPercentage = Number(item.discountPercentage || 0);
                return discountAmount === 0 && discountPercentage === 0;
            }) || []
        );
    }, [membership.membershipItems]);

    const discountedItems = React.useMemo(() => {
        return (
            membership.membershipItems?.filter(item => {
                const discountAmount = Number(item.discountAmount || 0);
                const discountPercentage = Number(item.discountPercentage || 0);
                return discountAmount > 0 || discountPercentage > 0;
            }) || []
        );
    }, [membership.membershipItems]);

    // Calculate total for regular items
    const regularTotal = React.useMemo(() => {
        return regularItems.reduce((sum, item) => {
            const amount = Number(item.amount);
            const quantity = Number(item.quantity);
            return sum + (item.unlimited ? amount : amount * quantity);
        }, 0);
    }, [regularItems]);

    // Check if there are discounted items
    const hasDiscountedItems = discountedItems.length > 0;

    // Calculate membership total (sum of amounts only)
    /*
    const membershipTotal = React.useMemo(() => {
        const regularAmounts = regularItems.reduce((sum, item) => sum + Number(item.amount), 0);
        const discountedAmounts = discountedItems.reduce((sum, item) => sum + Number(item.amount), 0);
        return regularAmounts + discountedAmounts;
    }, [regularItems, discountedItems]);
    */

    // Helper functions for discounted items
    const getDiscountDisplay = (item: MembershipItem): string => {
        const discountAmount = Number(item.discountAmount || 0);
        const discountPercentage = Number(item.discountPercentage || 0);

        if (discountAmount > 0) {
            return `-$${discountAmount.toFixed(2)}`;
        }
        if (discountPercentage > 0) {
            return `-${discountPercentage}%`;
        }
        return '-';
    };

    /* getFinalPrice - temporarily not used
    const getFinalPrice = (item: MembershipItem): number => {
        const amount = Number(item.amount);
        const discountAmount = Number(item.discountAmount || 0);
        const discountPercentage = Number(item.discountPercentage || 0);

        if (discountAmount > 0) {
            return amount - discountAmount;
        }
        if (discountPercentage > 0) {
            return amount * (1 - discountPercentage / 100);
        }
        return amount;
    };
    */

    if (!open) return null;

    const headersMembershipDetail = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim)}>Item</TableCell>
            {/* <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Price
            </TableCell> */}
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Quantity
            </TableCell>
            {/* <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Total
            </TableCell> */}
        </>
    );

    const rowsMembershipDetail = (
        <>
            {regularItems.map(item => (
                <TableRow key={item.id}>
                    <TableCell className={clsx(tableStyles.cell, classes.cellName, classes.cellSlim)}>
                        {item.itemName}
                        <Typography className={classes.itemType}>{item.itemType}</Typography>
                    </TableCell>
                    {/* <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${Number(item.amount).toFixed(2)}
                    </TableCell> */}
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        {item.unlimited ? 'Unlimited' : item.quantity}
                    </TableCell>
                    {/* <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${Number(item.total).toFixed(2)}
                    </TableCell> */}
                </TableRow>
            ))}
        </>
    );

    // Headers for discounted items table
    const headersDiscountedItems = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim)}>Item</TableCell>
            {/* <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Price
            </TableCell> */}
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Discount
            </TableCell>
            {/* Final Price column - temporarily commented out
            <TableCell className={clsx(tableStyles.headerCell, classes.headerCellSlim, classes.rightAlign)}>
                Final Price
            </TableCell>
            */}
        </>
    );

    // Rows for discounted items table
    const rowsDiscountedItems = (
        <>
            {discountedItems.map(item => (
                <TableRow key={item.id}>
                    <TableCell className={clsx(tableStyles.cell, classes.cellName, classes.cellSlim)}>
                        {item.itemName}
                        <Typography className={classes.itemType}>{item.itemType}</Typography>
                    </TableCell>
                    {/* <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${Number(item.amount).toFixed(2)}
                    </TableCell> */}
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        {getDiscountDisplay(item)}
                    </TableCell>
                    {/* Final Price column - temporarily commented out
                    <TableCell className={clsx(tableStyles.cell, classes.cellSlim, classes.rightAlign)}>
                        ${getFinalPrice(item).toFixed(2)}
                    </TableCell>
                    */}
                </TableRow>
            ))}
        </>
    );

    const MobileItemDetail = ({ item }: { item: MembershipItem }) => (
        <Box
            mb={'10px'}
            borderBottom="1px solid #e0e0e0"
        >
            <Typography
                variant="h6"
                className={clsx(classes.subscriptionTypeItemLabel, classes.mobileItemLabel)}
            >
                {item.itemName}
            </Typography>
            <Typography className={clsx(classes.itemType, classes.mobileItemType)}>{item.itemType}</Typography>
            <Box
                display="flex"
                justifyContent="space-between"
                mt={'5px'}
                mb={'14px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Qty
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    {item.unlimited ? 'Unlimited' : item.quantity}
                </Box>
            </Box>
            {/* <Box
                display="flex"
                justifyContent="space-between"
                mb={'5px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Price
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    ${Number(item.amount).toFixed(2)}
                </Box>
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                mb={'14px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Total
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    ${Number(item.total).toFixed(2)}
                </Box>
            </Box> */}
        </Box>
    );

    const MobileDiscountedItemDetail = ({ item }: { item: MembershipItem }) => (
        <Box
            mb={'10px'}
            borderBottom="1px solid #e0e0e0"
        >
            <Typography
                variant="h6"
                className={clsx(classes.subscriptionTypeItemLabel, classes.mobileItemLabel)}
            >
                {item.itemName}
            </Typography>
            <Typography className={clsx(classes.itemType, classes.mobileItemType)}>{item.itemType}</Typography>
            {/* <Box
                display="flex"
                justifyContent="space-between"
                mt={'5px'}
                mb={'5px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Price
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    ${Number(item.amount).toFixed(2)}
                </Box>
            </Box> */}
            <Box
                display="flex"
                justifyContent="space-between"
                mt={'5px'}
                mb={'14px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Discount
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={400}
                >
                    {getDiscountDisplay(item)}
                </Box>
            </Box>
            {/* Final Price - temporarily commented out
            <Box
                display="flex"
                justifyContent="space-between"
                mb={'14px'}
            >
                <Box
                    fontSize={'14px'}
                    fontWeight={500}
                >
                    Final Price
                </Box>
                <Box
                    fontSize={'14px'}
                    fontWeight={600}
                >
                    ${getFinalPrice(item).toFixed(2)}
                </Box>
            </Box>
            */}
        </Box>
    );

    const mobileMembershipDetailTable = (
        <Box>
            {regularItems.length > 0 && (
                <>
                    <Typography
                        style={{
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 12,
                            marginTop: 8
                        }}
                    >
                        Items Included
                    </Typography>
                    {regularItems.map((item, index) => (
                        <MobileItemDetail
                            key={index}
                            item={item}
                        />
                    ))}
                    {/*!hasDiscountedItems && (
                        <Typography className={classes.total}>
                            Total: <Typography className={classes.totalValue}>${regularTotal.toFixed(2)}</Typography>
                        </Typography>
                    )}*/}
                </>
            )}

            {discountedItems.length > 0 && (
                <>
                    <Typography
                        style={{
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 12,
                            marginTop: regularItems.length > 0 ? 24 : 8
                        }}
                    >
                        Discounted Items
                    </Typography>
                    {discountedItems.map((item, index) => (
                        <MobileDiscountedItemDetail
                            key={index}
                            item={item}
                        />
                    ))}
                </>
            )}

            {membership.total && (
                <Typography
                    className={classes.total}
                    style={{ marginTop: 16 }}
                >
                    Membership Total:{' '}
                    <Typography className={classes.totalValue}>${Number(membership.total).toFixed(2)}</Typography>
                </Typography>
            )}
        </Box>
    );

    const desktopMembershipDetailTable = (
        <>
            {regularItems.length > 0 && (
                <>
                    <Typography className={classes.sectionTitle}>Items Included</Typography>
                    <Box className={classes.tableContainer}>
                        <Table
                            headers={headersMembershipDetail}
                            rows={rowsMembershipDetail}
                            loading={false}
                            withoutColor={true}
                        />
                    </Box>
                    {/*!hasDiscountedItems && (
                        <Typography className={classes.total}>
                            Total: <Typography className={classes.totalValue}>${regularTotal.toFixed(2)}</Typography>
                        </Typography>
                    )}*/}
                </>
            )}

            {discountedItems.length > 0 && (
                <>
                    <Typography
                        className={classes.sectionTitle}
                        style={{ marginTop: regularItems.length > 0 ? 32 : 16 }}
                    >
                        Discounted Items
                    </Typography>
                    <Box className={classes.tableContainer}>
                        <Table
                            headers={headersDiscountedItems}
                            rows={rowsDiscountedItems}
                            loading={false}
                            withoutColor={true}
                        />
                    </Box>
                </>
            )}

            {membership.total && (
                <Typography
                    className={classes.total}
                    style={{ marginTop: 16 }}
                >
                    Membership Total:{' '}
                    <Typography className={classes.totalValue}>${Number(membership.total).toFixed(2)}</Typography>
                </Typography>
            )}
        </>
    );

    const renderMembershipDetailTable = () => {
        return isMobile ? mobileMembershipDetailTable : desktopMembershipDetailTable;
    };

    return (
        <>
            <Box
                className={classes.backdrop}
                onClick={onClose}
            />
            <Box className={classes.modal}>
                <Box className={classes.header}>
                    <Typography className={classes.headerTitle}>Membership Details</Typography>
                    <Box className={classes.subscriptionType}>
                        {membership.enrollmentType === 'subscription' && (
                            <>
                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>Type</Typography>
                                    <Typography className={classes.subscriptionTypeItemValue}>
                                        <FontAwesomeIcon
                                            className={classes.subscriptionTypeItemIcon}
                                            icon={faRefresh}
                                        />
                                        Subscription
                                    </Typography>
                                </Box>
                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>Frequency</Typography>
                                    {membership.billingFrequency && (
                                        <Typography className={classes.subscriptionTypeItemValue}>
                                            {formatBillingFrequency(membership)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>
                                        Subscription Usage
                                    </Typography>
                                    <Typography className={classes.subscriptionTypeItemValue}>
                                        {getRolloverUsageDisplay()}
                                    </Typography>
                                </Box>
                            </>
                        )}
                        {membership.enrollmentType === 'package' && (
                            <>
                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>Type</Typography>
                                    <Typography className={classes.subscriptionTypeItemValue}>
                                        <FontAwesomeIcon
                                            className={classes.subscriptionTypeItemIcon}
                                            icon={faBoxTaped}
                                        />
                                        Package
                                    </Typography>
                                </Box>

                                <Box className={classes.subscriptionTypeItem}>
                                    <Typography className={classes.subscriptionTypeItemLabel}>
                                        Package Expiration
                                    </Typography>
                                    <Typography className={classes.subscriptionTypeItemValue}>
                                        {getPackageExpirationDisplay()}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>

                    <IconButton
                        className={classes.closeButton}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Box>

                {renderMembershipDetailTable()}
            </Box>
        </>
    );
};

export default MembershipDetailsModal;
